import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getTenantInfo, updateTenantInfo } from '../features/tenant/tenantSlice';
import { toast } from 'react-toastify';
import Loading from '../components/Loading';

const BusinessSettings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { tenant, limits, usage, loading } = useSelector((state) => state.tenant);
  const serviceType = user?.tenantId?.serviceType || 'full_pos';
  const isEcommerceLite = serviceType === 'ecommerce_lite';

  const [formData, setFormData] = useState({
    businessName: '',
    phone: '',
    address: '',
    logo: '',
    mpAccessToken: '',
    mpQRPosId: '',
    serviceType: ''
  });
  const [showMpToken, setShowMpToken] = useState(false);

  const [editMode, setEditMode] = useState(false);

  // Identidad del comercio (editable por el usuario)
  // Nota: la identidad del programa (nombre "Ventio" y favicon) es fija por código.
  const [brandName, setBrandName] = useState(() => localStorage.getItem('ticketLeftName') || tenant?.businessName || '');
  const [logoPreview, setLogoPreview] = useState(() => localStorage.getItem('ticketLogoData') || '');
  const [ticketDefaultNote, setTicketDefaultNote] = useState(() => {
    try { return localStorage.getItem('ticketDefaultNote') || ''; } catch { return ''; }
  });
  // Preferencias: bypass del MobileGuard y modo solo escritorio
  const [mobileBypass, setMobileBypass] = useState(() => {
    try { return localStorage.getItem('mobileGuardBypass') === '1'; } catch { return false; }
  });
  const [forceDesktop, setForceDesktop] = useState(() => {
    try { return (localStorage.getItem('forceDesktop') ?? '1') === '1'; } catch { return true; }
  });

  // Preferencias de impresión: método por defecto y auto-print
  const [printDefaultMethod, setPrintDefaultMethod] = useState(() => {
    try { return localStorage.getItem('printDefaultMethod') || 'ticket'; } catch { return 'ticket'; }
  });
  const [printAutoMode, setPrintAutoMode] = useState(() => {
    try { return (localStorage.getItem('printAutoMode') || 'true') === 'true'; } catch { return true; }
  });

  const [enableProductVariants, setEnableProductVariants] = useState(false);
  const [enableMercadoPagoIntegration, setEnableMercadoPagoIntegration] = useState(false);

  // Initialize localStorage with defaults if not set
  useEffect(() => {
    try {
      if (localStorage.getItem('printAutoMode') === null) {
        localStorage.setItem('printAutoMode', 'true');
        setPrintAutoMode(true);
      }
      if (localStorage.getItem('printDefaultMethod') === null) {
        localStorage.setItem('printDefaultMethod', 'ticket');
        setPrintDefaultMethod('ticket');
      }
    } catch (e) {
      console.error('Error initializing print settings:', e);
    }
  }, []);

  useEffect(() => {
    dispatch(getTenantInfo());
  }, [dispatch]);

  useEffect(() => {
    if (tenant) {
      setFormData({
        businessName: tenant.businessName || '',
        phone: tenant.phone || '',
        address: tenant.address || '',
        logo: tenant.logo || '',
        mpAccessToken: tenant.settings?.mercadoPagoPointToken || tenant.modules?.ecommerce?.ownShop?.mercadoPago?.accessToken || '',
        mpQRPosId: tenant.settings?.mercadoPagoQRPosId || '',
        serviceType: tenant.serviceType || 'full_pos'
      });
      setEnableProductVariants(tenant.settings?.enableProductVariants || false);
      setEnableMercadoPagoIntegration(tenant.settings?.enableMercadoPagoIntegration || false);
    }
  }, [tenant]);

  const onChange = (e) => {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const payload = {
      businessName: formData.businessName,
      phone: formData.phone,
      address: formData.address,
      serviceType: formData.serviceType,
      settings: {
        mercadoPagoPointToken: formData.mpAccessToken,
        mercadoPagoQRPosId: formData.mpQRPosId,
        enableMercadoPagoIntegration: enableMercadoPagoIntegration
      }
    };

    console.log('💾 Guardando configuración MP Point:', {
      tokenLength: formData.mpAccessToken?.length || 0,
      hasToken: !!formData.mpAccessToken,
      payload
    });

    dispatch(updateTenantInfo(payload))
      .unwrap()
      .then((response) => {
        console.log('✅ Respuesta del servidor:', response);
        toast.success('✅ Configuración guardada correctamente');
        setEditMode(false);
        // Reload tenant data to ensure UI reflects saved values
        dispatch(getTenantInfo());
      })
      .catch((err) => {
        console.error('❌ Error al guardar:', err);
        toast.error('Error al guardar: ' + err);
      });
  };


  const handleToggleVariants = () => {
    const newValue = !enableProductVariants;
    setEnableProductVariants(newValue);
    dispatch(updateTenantInfo({
      settings: {
        enableProductVariants: newValue
      }
    }));
    toast.success(`Variantes de productos ${newValue ? 'activadas' : 'desactivadas'}`);
  };

  const isUnlimited = (max) => !max || Number(max) === 0;

  const getUsagePercentage = (current, max) => {
    if (isUnlimited(max)) return 0; // sin límite: no mostrar progreso
    const pct = (Number(current || 0) / Number(max)) * 100;
    return Math.min(pct, 100);
  };

  const formatQuota = (current, max) => {
    const cur = Number(current || 0);
    // All plans now have a limit of 2 users
    if (isUnlimited(max)) return `${cur} / 2`;
    return `${cur} / ${Number(max)}`;
  };

  const formatTrialRemaining = (expiry, status) => {
    if (status !== 'trial' || !expiry) return null;
    const now = Date.now();
    const end = new Date(expiry).getTime();
    const diffMs = end - now;
    if (diffMs <= 0) return 'Finalizó';
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays >= 1) return `${diffDays} día(s) restantes`;
    return 'Menos de 24h restantes';
  };

  const getTrialProgress = (expiry, status, createdAt) => {
    if (status !== 'trial' || !expiry) return 0;
    const end = new Date(expiry).getTime();
    const now = Date.now();
    const defaultTrialMs = 30 * 24 * 60 * 60 * 1000; // 30 días por defecto
    const start = createdAt ? new Date(createdAt).getTime() : (end - defaultTrialMs);
    const total = Math.max(end - start, 1);
    const elapsed = Math.min(Math.max(now - start, 0), total);
    return Math.min(Math.max((elapsed / total) * 100, 0), 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return '#f56565';
    if (percentage >= 70) return '#ed8936';
    return '#48bb78';
  };

  const isProPlan = (tenant) => {
    if (!tenant) return false;
    try {
      // Check common locations where the plan name might be stored
      const candidates = [
        tenant.plan,
        tenant.planName,
        tenant.subscription && tenant.subscription.plan,
        tenant.subscriptionPlan,
        tenant.limits && tenant.limits.plan,
        tenant.product && tenant.product.name,
        tenant.billing && tenant.billing.plan
      ];
      const byName = candidates.some(c => typeof c === 'string' && c.toLowerCase().includes('pro')) || String(tenant.plan || '').toLowerCase() === 'pro';
      if (byName) return true;
      // Also detect a 'pro' plan by inspecting limits: pro has much larger limits than free/basic
      try {
        const lim = tenant.limits || {};
        const maxUsers = Number(lim.maxUsers || 0);
        const maxProducts = Number(lim.maxProducts || 0);
        const maxSales = Number(lim.maxSalesPerMonth || 0);
        // Heuristic: if limits meet or exceed pro tier thresholds, treat as pro
        if (maxUsers >= 15 || maxProducts >= 2000 || maxSales >= 10000) return true;
      } catch (e) { /* ignore */ }
      return false;
    } catch (e) {
      return false;
    }
  };

  if (loading && !tenant) {
    return <Loading />;
  }

  const containerStyle = {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto'
  };

  const headerStyle = {
    marginBottom: '30px'
  };

  const titleStyle = {
    fontSize: '28px',
    fontWeight: '700',
    color: '#2d3748',
    marginBottom: '10px'
  };

  const subtitleStyle = {
    fontSize: '14px',
    color: '#718096'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '20px',
    marginBottom: '30px'
  };

  const cardStyle = {
    background: '#ffffff',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    border: '1px solid #e2e8f0'
  };

  const cardTitleStyle = {
    fontSize: '18px',
    fontWeight: '600',
    color: '#2d3748',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  };

  const infoItemStyle = {
    marginBottom: '12px',
    display: 'flex',
    flexDirection: 'column'
  };

  const labelStyle = {
    fontSize: '12px',
    fontWeight: '600',
    color: '#718096',
    marginBottom: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  };

  const valueStyle = {
    fontSize: '16px',
    color: '#2d3748',
    fontWeight: '500'
  };

  const badgeStyle = (status) => ({
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    background: status === 'active' ? '#c6f6d5' : status === 'trial' ? '#feebc8' : '#fed7d7',
    color: status === 'active' ? '#22543d' : status === 'trial' ? '#7c2d12' : '#742a2a'
  });

  const usageBarContainerStyle = {
    marginTop: '8px',
    height: '8px',
    background: '#e2e8f0',
    borderRadius: '4px',
    overflow: 'hidden'
  };

  const usageBarStyle = (percentage, color) => ({
    height: '100%',
    width: `${percentage}%`,
    background: color,
    transition: 'all 0.3s'
  });

  const usageTextStyle = {
    fontSize: '14px',
    color: '#4a5568',
    marginTop: '8px'
  };

  const buttonStyle = (variant = 'primary') => ({
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '600',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s',
    background: variant === 'primary' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#e2e8f0',
    color: variant === 'primary' ? '#ffffff' : '#2d3748'
  });

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    outline: 'none',
    marginTop: '4px',
    boxSizing: 'border-box'
  };

  // (hooks movidos arriba del early return)


  const handleLogoFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setLogoPreview(dataUrl);
      localStorage.setItem('ticketLogoData', dataUrl);
      toast.success('Logo guardado para tickets');
    };
    reader.onerror = () => toast.error('No se pudo leer la imagen');
    reader.readAsDataURL(file);
  };


  const clearLogo = () => {
    try { localStorage.removeItem('ticketLogoData'); } catch { }
    setLogoPreview('');
    toast.success('Logo quitado');
  };


  const saveBrandName = () => {
    // Guardamos solo para tickets/encabezados; no alteramos el nombre del programa ni el título del sitio
    localStorage.setItem('ticketLeftName', brandName || '');
    toast.success('Nombre del comercio guardado');
  };

  const saveDefaultNote = () => {
    try {
      localStorage.setItem('ticketDefaultNote', ticketDefaultNote.trim());
      toast.success('Nota por defecto guardada');
    } catch (e) {
      toast.error('No se pudo guardar');
    }
  };

  const btnNav = {
    background: '#6c757d',
    color: 'white',
    border: 'none',
    padding: '8px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h1 style={titleStyle}>⚙️ Configuración del Comercio</h1>
          <div>
            <button style={btnNav} onClick={() => navigate('/dashboard')}>🏠 Volver al Dashboard</button>
          </div>
        </div>
        <p style={subtitleStyle}>Gestiona la información y configuración de tu negocio</p>
      </div>

      <div style={gridStyle}>
        {/* Identidad Visual */}
        <div style={cardStyle}>
          <h3 style={cardTitleStyle}>🎨 Identidad del comercio</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
            {/* Logo para tickets */}
            <div>
              <div style={infoItemStyle}>
                <span style={labelStyle}>Logo (tickets)</span>
                {logoPreview ? (
                  <img src={logoPreview} alt="logo" style={{ maxWidth: '180px', maxHeight: '100px', border: '1px solid #e2e8f0', borderRadius: 8 }} />
                ) : (
                  <div style={{ fontSize: 12, color: '#718096' }}>Sin logo cargado</div>
                )}
              </div>
              <input type="file" accept="image/*" onChange={(e) => handleLogoFile(e.target.files?.[0])} />
              {logoPreview && (
                <div style={{ marginTop: 8 }}>
                  <button onClick={clearLogo} style={buttonStyle('secondary')}>🗑️ Quitar logo</button>
                </div>
              )}
            </div>

            {/* Nombre de fantasía para tickets */}
            <div>
              <div style={infoItemStyle}>
                <span style={labelStyle}>Nombre del comercio (En Tickets)</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="text"
                    value={brandName}
                    onChange={(e) => setBrandName(e.target.value)}
                    placeholder="Ej: MI NEGOCIO"
                    style={{ ...inputStyle, marginTop: 0 }}
                  />
                  <button onClick={saveBrandName} style={buttonStyle('primary')}>💾</button>
                </div>
                <div style={{ fontSize: 12, color: '#718096', marginTop: 4 }}>
                  Cambia el nombre que aparece arriba a la izquierda en los tickets.
                </div>
              </div>
            </div>

            {/* Nota por defecto para tickets */}
            <div>
              <div style={infoItemStyle}>
                <span style={labelStyle}>📝 Nota por defecto en tickets <span style={{ textTransform: 'none', fontWeight: 400, letterSpacing: 0 }}>(opcional)</span></span>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <textarea
                    id="ticket-default-note"
                    value={ticketDefaultNote}
                    onChange={(e) => setTicketDefaultNote(e.target.value)}
                    placeholder="Ej: No se aceptan devoluciones. Gracias por su compra!"
                    maxLength={300}
                    rows={3}
                    style={{
                      ...inputStyle,
                      marginTop: 0,
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      lineHeight: '1.4',
                      flex: 1
                    }}
                  />
                  <button onClick={saveDefaultNote} title="Guardar nota" style={{ ...buttonStyle('primary'), whiteSpace: 'nowrap', marginTop: 0 }}>💾</button>
                </div>
                <div style={{ fontSize: 12, color: '#718096', marginTop: 4 }}>
                  Aparece al pie de todos los tickets no fiscales. Podés sobreescribirla por venta desde el modal de pago.
                </div>
              </div>
            </div>

          </div>

          {/* Preferencias de impresión */}
          {!isEcommerceLite && (
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px dashed #e2e8f0' }}>
              <label style={labelStyle}>Impresión</label>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ minWidth: 220 }}>
                  <div style={{ fontSize: 13, color: '#4a5568', marginBottom: 6 }}>Formato por defecto</div>
                  <select
                    value={printDefaultMethod}
                    onChange={(e) => {
                      const v = e.target.value;
                      try { localStorage.setItem('printDefaultMethod', v); } catch (err) { }
                      setPrintDefaultMethod(v);
                      toast.info('Método de impresión por defecto actualizado');
                    }}
                    style={inputStyle}
                  >
                    <option value="ticket">Ticket 80mm</option>
                    <option value="a4">Factura A4</option>
                    <option value="pdf">PDF (nueva pestaña)</option>
                  </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={printAutoMode}
                      onChange={(e) => {
                        const v = !!e.target.checked;
                        try { localStorage.setItem('printAutoMode', v ? 'true' : 'false'); } catch (err) { }
                        setPrintAutoMode(v);
                        toast.info('Auto-print actualizado');
                      }}
                    />
                    <span style={{ fontSize: 13, color: '#4a5568' }}>Imprimir automáticamente tras venta</span>
                  </label>
                </div>
              </div>
            </div>
          )}


          {/* Preferencias de dispositivo */}
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px dashed #e2e8f0' }}>
            <label style={labelStyle}>📱 Preferencias de Dispositivo (Navegador)</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={mobileBypass}
                  onChange={(e) => {
                    const v = !!e.target.checked;
                    localStorage.setItem('mobileGuardBypass', v ? '1' : '0');
                    setMobileBypass(v);
                    toast.info(`Bypass de móvil ${v ? 'activado' : 'desactivado'}`);
                  }}
                />
                <span style={{ fontSize: 13, color: '#4a5568' }}>Permitir uso en celulares (Bypass MobileGuard)</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={forceDesktop}
                  onChange={(e) => {
                    const v = !!e.target.checked;
                    localStorage.setItem('forceDesktop', v ? '1' : '0');
                    setForceDesktop(v);
                    toast.info(`Modo escritorio ${v ? 'forzado' : 'desactivado'}`);
                  }}
                />
                <span style={{ fontSize: 13, color: '#4a5568' }}>Forzar versión de escritorio en este dispositivo</span>
              </label>
            </div>
          </div>
        </div>

        {/* Configuración de Inventario (Variantes) */}
        <div style={cardStyle}>
          <h3 style={cardTitleStyle}>📦 Configuración de Inventario</h3>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: '600', color: '#2d3748', marginBottom: 4 }}>
                Variantes de Producto (Talles y Colores)
              </div>
              <div style={{ fontSize: 13, color: '#718096', maxWidth: '500px' }}>
                Habilitar esta opción permite asignar variantes como Talle, Color y Stock individual a tus productos. Ideal para indumentaria.
              </div>
            </div>

            <button
              onClick={handleToggleVariants}
              style={{
                ...buttonStyle(enableProductVariants ? 'primary' : 'secondary'),
                background: enableProductVariants ? '#48bb78' : '#e2e8f0',
                color: enableProductVariants ? 'white' : '#4a5568'
              }}
            >
              {enableProductVariants ? '✅ Activado' : '⚪ Desactivado'}
            </button>
          </div>
        </div>

        {/* Información del Comercio */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
              {tenant?.subscriptionStatus === 'trial' && tenant?.subscriptionExpiry && (
                <span
                  style={{
                    background: '#fff7ed',
                    color: '#7c2d12',
                    border: '1px solid #fbd38d',
                    padding: '6px 10px',
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden'
                  }}
                  title={`Estás en periodo de prueba: vence el ${new Date(tenant.subscriptionExpiry).toLocaleDateString('es-AR')} (${formatTrialRemaining(tenant.subscriptionExpiry, tenant.subscriptionStatus) || '—'})`}
                >
                  Estás en periodo de prueba: vence el {new Date(tenant.subscriptionExpiry).toLocaleDateString('es-AR')} ({formatTrialRemaining(tenant.subscriptionExpiry, tenant.subscriptionStatus) || '—'})
                </span>
              )}
              <h3 style={cardTitleStyle}>
                🏢 Información del Comercio
              </h3>
            </div>
            {!editMode && (
              <button onClick={() => setEditMode(true)} style={buttonStyle('secondary')}>
                ✏️ Editar
              </button>
            )}
          </div>

          {!editMode ? (
            <>
              <div style={infoItemStyle}>
                <span style={labelStyle}>Nombre del Comercio</span>
                <span style={valueStyle}>{tenant?.businessName}</span>
              </div>
              <div style={infoItemStyle}>
                <span style={labelStyle}>Tipo de Servicio</span>
                <span style={valueStyle}>
                  {tenant?.serviceType === 'restaurant' && '🍽️ Gastronómico (Restaurante/Bar)'}
                  {tenant?.serviceType === 'ecommerce_lite' && '🛍️ E-commerce Lite (Solo Tienda Online)'}
                  {(!tenant?.serviceType || tenant?.serviceType === 'full_pos') && '💼 Comercio General (POS Retail)'}
                </span>
              </div>
              <div style={infoItemStyle}>
                <span style={labelStyle}>Subdominio</span>
                <span style={valueStyle}>{tenant?.subdomain}.tupos.com</span>
              </div>
              <div style={infoItemStyle}>
                <span style={labelStyle}>Email</span>
                <span style={valueStyle}>{tenant?.email}</span>
              </div>
              <div style={infoItemStyle}>
                <span style={labelStyle}>Teléfono</span>
                <span style={valueStyle}>{tenant?.phone || 'No especificado'}</span>
              </div>
              <div style={infoItemStyle}>
                <span style={labelStyle}>Dirección</span>
                <span style={valueStyle}>{tenant?.address || 'No especificada'}</span>
              </div>

              {/* MP Token Read-only View */}
              {!isEcommerceLite && (
                <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px dashed #e2e8f0' }}>
                  <div style={infoItemStyle}>
                    <span style={labelStyle}>Integración Mercado Pago Point (Token)</span>
                    <span style={valueStyle}>
                      {/* Check new location first, then legacy form data */}
                      {tenant?.settings?.mercadoPagoPointToken || formData.mpAccessToken ? '••••••••••••••••••••••••' : 'No configurado'}
                    </span>
                    <div style={{ fontSize: 13, color: (tenant?.settings?.mercadoPagoPointToken || formData.mpAccessToken) ? '#48bb78' : '#e53e3e', marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {(tenant?.settings?.mercadoPagoPointToken || formData.mpAccessToken)
                        ? <>✅ <span>Token activo para terminales.</span></>
                        : <>⚠️ <span>Sin configurar.</span></>}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <form onSubmit={handleSubmit}>
              <div style={infoItemStyle}>
                <label style={labelStyle}>Nombre del Comercio</label>
                <input
                  type="text"
                  name="businessName"
                  value={formData.businessName}
                  onChange={onChange}
                  style={inputStyle}
                />
              </div>
              <div style={infoItemStyle}>
                <label style={labelStyle}>Teléfono</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={onChange}
                  style={inputStyle}
                />
              </div>
              <div style={infoItemStyle}>
                <label style={labelStyle}>Dirección</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={onChange}
                  style={inputStyle}
                />
              </div>
              <div style={infoItemStyle}>
                <label style={labelStyle}>Tipo de Servicio</label>
                <select
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={onChange}
                  style={inputStyle}
                >
                  <option value="full_pos">💼 Comercio General (POS Retail)</option>
                  <option value="restaurant">🍽️ Gastronómico (Restaurante/Bar)</option>
                  <option value="ecommerce_lite">🛍️ E-commerce Lite (Solo Tienda Online)</option>
                </select>
              </div>

              {/* MP Token Edit View */}
              {!isEcommerceLite && (
                <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px dashed #e2e8f0' }}>
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}>
                      <input
                        type="checkbox"
                        checked={enableMercadoPagoIntegration}
                        onChange={(e) => setEnableMercadoPagoIntegration(e.target.checked)}
                        style={{ marginRight: 10, width: 20, height: 20, cursor: 'pointer' }}
                      />
                      <div>
                        <div style={{ fontWeight: 'bold', color: enableMercadoPagoIntegration ? '#2d3748' : '#718096' }}>
                          Activar Integración Automática con Mercado Pago
                        </div>
                        <div style={{ fontSize: 13, color: '#718096' }}>
                          Permite enviar el monto a pagar a terminales Point y generar QR dinámicos conectados a tu cuenta.
                        </div>
                      </div>
                    </label>
                  </div>

                  {enableMercadoPagoIntegration && (
                    <div style={{ paddingLeft: 10, borderLeft: '3px solid #63b3ed' }}>
                      <div style={infoItemStyle}>
                        <label style={labelStyle}>Access Token (Mercado Pago Point)</label>
                        <div style={{ position: 'relative' }}>
                          <input
                            type={showMpToken ? 'text' : 'password'}
                            name="mpAccessToken"
                            value={formData.mpAccessToken}
                            onChange={onChange}
                            style={{ ...inputStyle, paddingRight: '40px' }}
                            placeholder="APP_USR-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                            autoComplete="off"
                          />
                          <button
                            type="button"
                            onClick={() => setShowMpToken(!showMpToken)}
                            style={{
                              position: 'absolute',
                              right: '10px',
                              top: '50%',
                              transform: 'translateY(-30%)',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '16px',
                              color: '#718096'
                            }}
                            title={showMpToken ? 'Ocultar' : 'Mostrar'}
                          >
                            {showMpToken ? '🚫' : '👁️'}
                          </button>
                        </div>
                        <div style={{ fontSize: 12, color: '#718096', marginTop: 4, lineHeight: '1.5' }}>
                          Token de producción para conectar tus terminales físicas Point y pagos QR.
                          <br />
                          <a
                            href="https://www.mercadopago.com.ar/developers/panel/app"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#3182ce', textDecoration: 'underline' }}
                          >
                            📱 Obtener Access Token aquí
                          </a>
                          {' '}(Sección: Credenciales de producción → Access Token)
                        </div>
                      </div>

                      {/* QR POS ID Field */}
                      <div style={infoItemStyle}>
                        <label style={labelStyle}>ID de Caja QR (Mercado Pago)</label>
                        <input
                          type="text"
                          name="mpQRPosId"
                          value={formData.mpQRPosId}
                          onChange={onChange}
                          style={inputStyle}
                          placeholder="QRPRINCIPAL (recomendado)"
                        />
                        <div style={{ fontSize: 12, color: '#718096', marginTop: 4, lineHeight: '1.5' }}>
                          <strong>Configuración recomendada:</strong>
                          <br />
                          1. Crea una caja en{' '}
                          <a
                            href="https://www.mercadopago.com.ar/point/integration"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#3182ce', textDecoration: 'underline' }}
                          >
                            Mercado Pago Point
                          </a>
                          <br />
                          2. Asígnale el External ID: <code style={{ background: '#edf2f7', padding: '2px 4px', borderRadius: '3px' }}>QRPRINCIPAL</code>
                          <br />
                          3. Ingresa <code style={{ background: '#edf2f7', padding: '2px 4px', borderRadius: '3px' }}>QRPRINCIPAL</code> en este campo
                          <br />
                          <em style={{ color: '#a0aec0' }}>También puedes usar el ID numérico de la caja si prefieres.</em>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                <button type="submit" style={buttonStyle('primary')}>
                  💾 Guardar Cambios
                </button>
                <button type="button" onClick={() => setEditMode(false)} style={buttonStyle('secondary')}>
                  ❌ Cancelar
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Estado de Suscripción */}
        {tenant?.subscriptionStatus !== 'active' && (
          <div style={cardStyle}>
            <h3 style={cardTitleStyle}>
              💎 Estado de Suscripción
            </h3>

            <div style={infoItemStyle}>
              <span style={labelStyle}>Plan Actual</span>
              <span style={valueStyle}>{tenant?.plan?.toUpperCase()}</span>
            </div>

            <div style={infoItemStyle}>
              <span style={labelStyle}>Estado</span>
              <span style={badgeStyle(tenant?.subscriptionStatus)}>
                {tenant?.subscriptionStatus === 'trial' && '🆓 PRUEBA'}
                {tenant?.subscriptionStatus === 'active' && '✅ ACTIVO'}
                {tenant?.subscriptionStatus === 'suspended' && '⚠️ SUSPENDIDO'}
              </span>
            </div>

            <div style={infoItemStyle}>
              <span style={labelStyle}>
                {tenant?.subscriptionStatus === 'trial' ? 'Fin de Prueba' : 'Próxima Renovación'}
              </span>
              <span style={valueStyle}>
                {tenant?.subscriptionExpiry ? new Date(tenant.subscriptionExpiry).toLocaleDateString('es-AR') : 'N/A'}
                {tenant?.subscriptionStatus === 'trial' && tenant?.subscriptionExpiry && (
                  <>
                    {' '}
                    <span style={{ color: '#718096', fontSize: 13 }}>
                      ({Math.max(0, Math.ceil((new Date(tenant.subscriptionExpiry).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))} día(s) restantes)
                    </span>
                  </>
                )}
              </span>
            </div>

            <button
              onClick={() => navigate('/plans')}
              style={{
                ...buttonStyle('primary'),
                width: '100%',
                marginTop: '16px'
              }}
            >
              {isProPlan(tenant) ? '💎 Ver Opciones de Pago' : '🚀 Actualizar Plan'}
            </button>
          </div>
        )}
      </div>

      {/* Uso de Recursos */}
      <div style={cardStyle}>
        <h3 style={cardTitleStyle}>
          📊 Uso de Recursos
        </h3>

        <div style={gridStyle}>
          {/* Usuarios */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={labelStyle}>👥 Usuarios</span>
              <span style={usageTextStyle}>{formatQuota(usage?.users, limits?.maxUsers)}</span>
            </div>
            <div style={usageBarContainerStyle}>
              <div
                style={usageBarStyle(
                  getUsagePercentage(usage?.users || 0, limits?.maxUsers),
                  getUsageColor(getUsagePercentage(usage?.users || 0, limits?.maxUsers))
                )}
              />
            </div>
          </div>

          {/* Tiempo restante de prueba (reemplaza Productos) */}
          {tenant?.subscriptionStatus === 'trial' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={labelStyle}>⏳ Prueba</span>
                <span style={usageTextStyle}>{formatTrialRemaining(tenant?.subscriptionExpiry, tenant?.subscriptionStatus) || '—'}</span>
              </div>
              <div style={{ marginTop: 8, fontSize: '13px', color: '#718096' }}>
                Disfrutá todas las funciones durante el período de prueba.
              </div>
              <div style={usageBarContainerStyle}>
                {(() => {
                  const pct = getTrialProgress(tenant?.subscriptionExpiry, tenant?.subscriptionStatus, tenant?.createdAt);
                  return (
                    <div
                      style={usageBarStyle(
                        pct,
                        getUsageColor(pct)
                      )}
                    />
                  );
                })()}
              </div>
            </div>
          )}

          {/* Ventas del Mes */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={labelStyle}>💰 Ventas Este Mes</span>
              <span style={usageTextStyle}>{formatQuota(usage?.salesThisMonth, limits?.maxSalesPerMonth)}</span>
            </div>
            <div style={usageBarContainerStyle}>
              <div
                style={usageBarStyle(
                  getUsagePercentage(usage?.salesThisMonth || 0, limits?.maxSalesPerMonth),
                  getUsageColor(getUsagePercentage(usage?.salesThisMonth || 0, limits?.maxSalesPerMonth))
                )}
              />
            </div>
          </div>
        </div>
      </div>
    </div >
  );
};

export default BusinessSettings;
